import apiClient from '@/services/api'

const cache = new Map<string, string>()

function normalizeInstagramUrl(url: string) {
  const match = url.match(/instagram\.com\/(reel|p|tv)\/([A-Za-z0-9_-]+)/i)
  if (!match) return url.split('?')[0]
  return `https://www.instagram.com/${match[1]}/${match[2]}/`
}

export function isGenericMediaFallback(url?: string | null) {
  if (!url) return true
  return url.includes('unsplash.com/photo-1600585154340')
}

async function fetchFromBackend(socialUrl: string, videoId?: number): Promise<string | null> {
  try {
    const res = await apiClient.get<{ data?: { thumbnailUrl?: string } }>('/media/instagram-oembed', {
      params: { url: socialUrl, videoId },
    })
    const thumb = res.data?.data?.thumbnailUrl
    return typeof thumb === 'string' && thumb !== '' ? thumb : null
  } catch {
    return null
  }
}

function fetchViaJsonp(socialUrl: string): Promise<string | null> {
  const normalized = normalizeInstagramUrl(socialUrl)

  return new Promise((resolve) => {
    const callbackName = `__ig_oembed_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

    const cleanup = (result: string | null) => {
      window.clearTimeout(timer)
      delete (window as unknown as Record<string, unknown>)[callbackName]
      script.remove()
      resolve(result)
    }

    const timer = window.setTimeout(() => cleanup(null), 10_000)

    ;(window as unknown as Record<string, unknown>)[callbackName] = (data: {
      thumbnail_url?: string
    }) => {
      cleanup(data?.thumbnail_url ?? null)
    }

    const script = document.createElement('script')
    script.src = `https://api.instagram.com/oembed?url=${encodeURIComponent(normalized)}&callback=${callbackName}`
    script.async = true
    script.onerror = () => cleanup(null)
    document.head.appendChild(script)
  })
}

/**
 * Fetch Instagram reel/post thumbnail via backend oEmbed proxy (preferred) or JSONP fallback.
 */
export async function fetchInstagramThumbnail(
  socialUrl: string,
  videoId?: number,
): Promise<string | null> {
  const normalized = normalizeInstagramUrl(socialUrl)
  const cacheKey = videoId ? `${videoId}:${normalized}` : normalized
  const cached = cache.get(cacheKey)
  if (cached) return cached

  const fromBackend = await fetchFromBackend(normalized, videoId)
  if (fromBackend) {
    cache.set(cacheKey, fromBackend)
    return fromBackend
  }

  const fromJsonp = await fetchViaJsonp(normalized)
  if (fromJsonp) cache.set(cacheKey, fromJsonp)

  return fromJsonp
}

export function prefetchInstagramThumbnail(socialUrl: string) {
  void fetchInstagramThumbnail(socialUrl)
}
